import { jsx as _jsx } from "react/jsx-runtime";
/**
 * BlockPinButton — hover-revealed pin toggle for a block card sitting
 * inside a Stack or non-Primary Collection.
 *
 * Mirrors the StackBlockView / CollectionBlockView pin-button pattern:
 * a small dark circle in the card's top-LEFT corner that fades in on
 * hover, stays visible when pinned, and toggles the block's pin in
 * its immediate parent Collection (a Stack is also a Collection) on
 * click. No popover — there's exactly one destination (the parent
 * Collection's default surface), so an intermediate menu would be
 * pure friction.
 *
 * Placement (top-LEFT) is the empty counter-corner to the date chip
 * (top-right) — the two affordances never collide. The button itself
 * lives as a sibling of `.block-view` inside the host, so its
 * positioning is anchored to the visible card area (the host is the
 * card's positioning context); it's outside the `.block-view`'s
 * `overflow: hidden` so an edge halo can't get clipped.
 *
 * Hidden during multiselect (the selection checkbox is the active
 * affordance), while the block is the modal target (the modal's own
 * toolbar owns pin), and while the card is still pending.
 *
 * Pin destination derives from the block's own `collectionId`, NOT
 * from the surrounding Stream's `PinSlotContext`: a block unfurled
 * inside a Stack lives in the Stack (`block.collectionId === stack.id`)
 * even though the surrounding Stream's pin slot points at the Stack's
 * parent Collection. Users expect "pin this block" inside an open
 * Stack to pin within that Stack, not in the parent grid.
 */
import React, { memo, useCallback } from "react";
import { faThumbtack } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { showToast } from "@shared/client/components/toast/show-toast";
import { MAX_PINS_PER_SURFACE } from "@shared/types/pin";
import { togglePinWithSync, usePinList } from "@/state/pin-store";
export const BlockPinButton = memo(({ blockId, parent }) => {
    const pins = usePinList({ type: "collection", id: parent.id }, "default");
    const isPinned = pins.some((p) => p.type === "block" && p.id === blockId);
    const handleClick = useCallback((e) => {
        e.stopPropagation();
        void togglePinWithSync({ type: "collection", id: parent.id }, "default", { type: "block", id: blockId }).then((result) => {
            if (result.ok === false && result.reason === "max") {
                showToast.warning(`You cannot pin more than ${MAX_PINS_PER_SURFACE} items here`);
            }
        });
    }, [blockId, parent.id]);
    return (_jsx("div", { className: [
            "block-view-host__pin-anchor",
            isPinned ? "block-view-host__pin-anchor--pinned" : "",
        ]
            .filter(Boolean)
            .join(" "), onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), children: _jsx("button", { type: "button", className: [
                "block-view-host__pin-btn",
                isPinned ? "block-view-host__pin-btn--pinned" : "",
            ]
                .filter(Boolean)
                .join(" "), "aria-label": isPinned ? `Unpin from ${parent.title}` : `Pin to ${parent.title}`, "aria-pressed": isPinned, onClick: handleClick, children: _jsx(FaIcon, { icon: faThumbtack }) }) }));
});
